import { AppBar, useMediaQuery, useTheme } from '@material-ui/core';
import React, { useContext } from 'react';
import { shallowEqual } from 'react-redux';

import { CLIENT_DETAILS_PAGE, CRM_CLIENTS_PAGE } from 'src/constants';
import {
  BANNER_HEIGHT,
  DESKTOP_APP_BAR_HEIGHT,
  MOBILE_APP_BAR_HEIGHT,
} from 'src/constants/uiConsts';
import { RouteContext, useFeatureFlags } from 'src/context';
import { ContextBarLayout } from 'src/features/context-bar/ContextBarLayout';
import { useImportProgressNotifier } from 'src/hooks/useImportProgressNotifier';
import { useAppDispatch, useAppSelector } from 'src/hooks/useStore';
import AuthModal from 'src/legacy/components/Auth/AuthModal';
import { Banner } from 'src/legacy/components/Banner';
import { CommandBar } from 'src/legacy/components/CommandBar';
import { GeneralCommandbarActionsInitializer } from 'src/legacy/components/CommandBar/GeneralCommandbarActionsInitializer';
import { CompleteExternalActionModal } from 'src/legacy/components/Modals';
import Navbar from 'src/legacy/components/Navbar';
import { ShortcutsSidebar } from 'src/legacy/components/ShortcutsDrawer';
import Sidebar from 'src/legacy/components/Sidebar';
import { WhatsNew } from 'src/legacy/components/WhatsNew/WhatsNew';
import { RootState } from 'src/store/reduxTypes';
import { updateSearchValueAction } from 'src/store/ui/actions';
import * as Colors from 'src/theme/colors';

import { globalCss } from '../globalCss';
import { HelpCenterModal } from '../HelpCenterModal';

import { HeaderSlotProvider, useHeaderSlot } from './HeaderSlotContext';
import { ShellProvider, useShell } from './ShellContext';

const PAGES_WITH_RETAINED_SEARCH = [
  CRM_CLIENTS_PAGE.path,
  CLIENT_DETAILS_PAGE.path,
];

/**
 * Inner component that consumes ShellContext and HeaderSlotContext.
 * Separated so the providers are established before any consumer hooks run.
 */
const AppShellInner: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { EnableContextBarVisualUpgrades } = useFeatureFlags();
  const {
    drawerWidth,
    sidebarOpen,
    openMobileSidebar,
    closeMobileSidebar,
    showContextBar,
    contextBarContentMinWidth,
  } = useShell();
  const { headerSlot, ignoreHeaderPadding, showSidebar } = useHeaderSlot();
  const { pathname } = useContext(RouteContext);
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const rootRef = React.useRef<HTMLDivElement>(null);

  // Breakpoints matching the original Layout's responsive behavior:
  // down('xs') = max-width 599.95px (mobile navbar height)
  // up('md') = min-width 960px (desktop drawer padding)
  const isXsDown = useMediaQuery(theme.breakpoints.down('xs'));
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));

  const {
    loaded: userLoaded,
    loading: userLoading,
    isClient,
  } = useAppSelector(
    (state: RootState) => ({
      loaded: state.user.loaded,
      loading: state.user.loading,
      isClient: state.user.isClient,
    }),
    shallowEqual,
  );

  const includeContextBar =
    EnableContextBarVisualUpgrades && showContextBar && !isClient;

  const bannerOptions = useAppSelector(
    (state: RootState) => state.ui.bannerOptions,
  );

  const showBanner = Boolean(bannerOptions);

  // headerSlot === undefined → no page has set a header (show default navbar)
  // headerSlot === null → page explicitly hides navbar (fullbleed)
  // headerSlot === <ReactNode> → custom header content
  const hasNavbar = headerSlot !== null;

  const navbarHeight = isXsDown
    ? MOBILE_APP_BAR_HEIGHT
    : includeContextBar
      ? 60
      : DESKTOP_APP_BAR_HEIGHT;
  const appBarHeight =
    (hasNavbar ? navbarHeight : 0) + (showBanner ? BANNER_HEIGHT : 0);

  const appBarStyle: React.CSSProperties = {
    backgroundColor: theme.palette.common.white,
    color: theme.palette.common.black,
    boxShadow: 'none',
    zIndex: theme.zIndex.drawer + 1,
    borderBottom: hasNavbar
      ? `1px solid ${Colors.DividersAndCardBorders}`
      : 'none',
    height: hasNavbar ? navbarHeight : 0,
    top: showBanner && !includeContextBar ? BANNER_HEIGHT : undefined,
    paddingLeft: isMdUp && showSidebar ? drawerWidth : 0,
  };
  if (!includeContextBar) {
    appBarStyle.left = '50%';
    appBarStyle.transform = 'translateX(-50%)';
  }

  // Header slot cleanup is handled by PageShell and LayoutShim unmount effects.
  // An AppShell-level clearHeader on pathname change would race against child
  // mount effects (React fires child effects before parent effects), overriding
  // the new page's setHeader call.

  // Search bar should persist query between client list and client details.
  // Reset when navigating away from those pages.
  React.useEffect(() => {
    if (!PAGES_WITH_RETAINED_SEARCH.some((page) => pathname.startsWith(page))) {
      dispatch(updateSearchValueAction(''));
    }
    return () => {
      if (
        !PAGES_WITH_RETAINED_SEARCH.some((page) => pathname.startsWith(page))
      ) {
        dispatch(updateSearchValueAction(''));
      }
    };
  }, [pathname, dispatch]);

  // Reset scroll position when navigating between pages.
  // AppShell's root div is the scroll container (overflowY: auto),
  // so window.scrollTo in client.js doesn't affect it.
  React.useEffect(() => {
    if (rootRef.current) {
      rootRef.current.scrollTop = 0;
    }
  }, [pathname]);

  useImportProgressNotifier();

  const showPrimarySidebarMobile = React.useCallback(() => {
    openMobileSidebar();
  }, [openMobileSidebar]);

  const hidePrimarySidebarMobile = React.useCallback(() => {
    closeMobileSidebar();
  }, [closeMobileSidebar]);

  const bannerStyle: React.CSSProperties = includeContextBar
    ? { zIndex: theme.zIndex.drawer + 2 }
    : {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: theme.zIndex.drawer + 2,
      };

  const shellContent = (
    <>
      <AppBar
        position={includeContextBar ? 'sticky' : 'fixed'}
        style={appBarStyle}
      >
        {hasNavbar && (
          <Navbar
            userLoaded={userLoaded}
            isClient={isClient}
            userLoading={userLoading}
            onOpenSidebarMobile={showPrimarySidebarMobile}
            showSearchbar={false}
            showNavbarAuthActions
            headerContent={headerSlot || undefined}
            ignoreHeaderPadding={ignoreHeaderPadding}
          />
        )}
      </AppBar>
      {showSidebar && (
        <Sidebar
          onMobileClose={hidePrimarySidebarMobile}
          openMobile={sidebarOpen}
        />
      )}
      {children}
    </>
  );

  return (
    <div
      ref={rootRef}
      className="flex flex-col h-dvh box-border"
      style={{
        fontFamily: theme.typography.fontFamily,
        paddingTop: includeContextBar ? undefined : appBarHeight,
        overflowY: includeContextBar ? undefined : isXsDown ? 'hidden' : 'auto',
      }}
    >
      {bannerOptions ? (
        <div style={bannerStyle}>
          <Banner {...bannerOptions} />
        </div>
      ) : null}
      <div className={includeContextBar ? 'flex flex-1 min-h-0' : 'flex-1'}>
        <CommandBar>
          {includeContextBar ? (
            <ContextBarLayout contentMinWidth={contextBarContentMinWidth}>
              {shellContent}
            </ContextBarLayout>
          ) : (
            shellContent
          )}
          <HelpCenterModal />
          <AuthModal />
          {userLoaded && (
            <>
              <CompleteExternalActionModal />
              <GeneralCommandbarActionsInitializer />
              <WhatsNew />
            </>
          )}
          <ShortcutsSidebar />
        </CommandBar>
      </div>
    </div>
  );
};

export interface AppShellProps {
  children: React.ReactNode;
}

const APPSHELL_RESET_ID = 'appshell-reset';

const AppShell: React.FC<AppShellProps> = ({ children }) => {
  // Inject critical CSS resets that the design-system normally provides via
  // an inline <style> tag in production SSR. On local dev the design-system
  // rules live inside a @layer and don't override browser defaults reliably.
  React.useLayoutEffect(() => {
    if (document.getElementById(APPSHELL_RESET_ID)) return;
    const style = document.createElement('style');
    style.id = APPSHELL_RESET_ID;
    style.textContent = globalCss.join('');
    document.head.appendChild(style);
  }, []);

  return (
    <ShellProvider>
      <HeaderSlotProvider>
        <AppShellInner>{children}</AppShellInner>
      </HeaderSlotProvider>
    </ShellProvider>
  );
};

export default AppShell;
